import { useState, useMemo, useEffect } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { Formik, Form } from 'formik'
import { Users } from '@phosphor-icons/react'
import * as Sentry from '@sentry/react'
import { toZonedTime } from 'date-fns-tz'
import { useTranslation } from 'react-i18next'

import ModalContainer from '../../../ModalContainer/ModelContainer'
import {
  VistorModalTypes,
  mapValuesToGuestData,
  mapValuesToVendorData,
  mapGuestDetailsToFormValues,
} from '../utils'
import ModalButtons from '../ModalButtons'
import Stepper from '../../../Stepper/Stepper'
import { selectActiveBuilding } from '../../../../reducers/buildingSelectorReducer'
import { useSnackbar } from '../../../../helpers/hooks/useSnackbar'
import API from '../../../../api/api'
import VisitorLoader from '../VisitorDetailsModal/VisitorLoader'

import SecondStep from './SecondStep'
import FirstStep from './FirstStep'
import { createValidationSchema } from './createValidationSchema'
import { getVisitorCreateErrorMessage } from './getVisitorCreateErrorMessage'

const getInitialValues = (t, timezone) => {
  const now = toZonedTime(new Date(), timezone)
  const fifteenMinutesLater = new Date(now.getTime() + 15 * 60000)

  return {
    resident: '',
    clientId: '',
    visitorName: '',
    visitorPhone: '',
    visitorEmail: '',
    visitorCompany: '',
    coi: null,
    visitorType: {
      id: '',
      text: '',
    },
    permissionType: { text: t('visitors:permissions.oneTime'), id: 'custom' },
    visitorId: '',
    timeStart: now,
    timeEnd: fifteenMinutesLater,
    dueDate: now,
    visitorNote: '',
    isVisitorNoteOpen: false,
    selectedDays: [],
    monthlyPermission: '',
    keyAccess: false,
    keylessAccess: false,
    customAccess: false,
    pinCode: false,
    startOfDay: null,
    endOfDay: null,
  }
}

const NewVisitorFormModal = ({
  isOpen,
  visitorModalType,
  toggleNewVisitorModalType,
  selectedBuilding,
  editingVisitor,
  onCreateSuccess,
}) => {
  const { t } = useTranslation(['visitors'])
  const { displaySnackbar } = useSnackbar()
  const [currentStep, setCurrentStep] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [isPrefillLoading, setIsPrefillLoading] = useState(false)
  const [initialValues, setInitialValues] = useState(
    getInitialValues(t, selectedBuilding?.timezone),
  )

  const defaultInitialValues = useMemo(
    () => getInitialValues(t, selectedBuilding?.timezone),
    [t, selectedBuilding?.timezone],
  )

  const isGuestModal = [VistorModalTypes.OneTimeGuest, VistorModalTypes.RecurringGuest].includes(
    visitorModalType,
  )
  const isVendorModal = [VistorModalTypes.OneTimeVendor, VistorModalTypes.RecurringVendor].includes(
    visitorModalType,
  )

  const isRecuringType = [
    VistorModalTypes.RecurringGuest,
    VistorModalTypes.RecurringVendor,
  ].includes(visitorModalType)
  const isUpdate =
    !!editingVisitor &&
    typeof editingVisitor === 'object' &&
    !Array.isArray(editingVisitor) &&
    Object.prototype.hasOwnProperty.call(editingVisitor, 'id')

  const isFirstStep = currentStep === 0
  const isSecondStep = currentStep === 1

  const prefix = isGuestModal ? t('visitors:modalTypes.guest') : t('visitors:modalTypes.vendor')
  const modalHeader = isUpdate
    ? t('visitors:form.titleEdit', { type: prefix })
    : t('visitors:form.titleAdd', { type: prefix })
  const firstStepName = t('visitors:form.stepInfo', { type: prefix })
  const firstStepLabelPrefix = prefix
  const submitButtonLabel = isUpdate
    ? t('visitors:form.submitUpdate', { type: prefix })
    : t('visitors:form.submitCreate', { type: prefix })

  const primaryButtonText = isRecuringType
    ? isFirstStep
      ? t('visitors:form.next')
      : submitButtonLabel
    : submitButtonLabel

  const modalTypeToGoBackTo = isGuestModal
    ? VistorModalTypes.GuestTypeModal
    : VistorModalTypes.VendorTypeModal
  const snackbarVisitorType = isGuestModal
    ? t('visitors:snackbar.typeGuest')
    : t('visitors:snackbar.typeVendor')

  const validationSchema = createValidationSchema(t, isGuestModal)

  useEffect(() => {
    setCurrentStep(0)
  }, [visitorModalType, editingVisitor?.id])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    if (!isUpdate || !isGuestModal) {
      setInitialValues(defaultInitialValues)
      return
    }

    const fetchGuestDetails = async () => {
      setIsPrefillLoading(true)

      try {
        const { data } = await API.getGuestDetails(editingVisitor.id)

        const guestStatus = data?.visitor?.status
        if (guestStatus === 'banned' || guestStatus === 'suspended') {
          displaySnackbar(t('visitors:snackbar.cannotEditBannedOrSuspended'), true)
          toggleNewVisitorModalType()
          return
        }

        setInitialValues(mapGuestDetailsToFormValues(t, data, selectedBuilding?.timezone))
      } catch (err) {
        Sentry.captureException(err)
        displaySnackbar(t('visitors:snackbar.genericError'), true)
        toggleNewVisitorModalType()
      } finally {
        setIsPrefillLoading(false)
      }
    }

    fetchGuestDetails()
  }, [
    defaultInitialValues,
    displaySnackbar,
    editingVisitor,
    isGuestModal,
    isOpen,
    isUpdate,
    selectedBuilding?.timezone,
    toggleNewVisitorModalType,
  ])

  const handleSubmit = async (values) => {
    setIsLoading(true)

    const data = isGuestModal
      ? mapValuesToGuestData(values, isRecuringType, selectedBuilding.timezone)
      : mapValuesToVendorData(
          values,
          isRecuringType,
          selectedBuilding.id,
          selectedBuilding.timezone,
        )

    try {
      if (isUpdate && isGuestModal) {
        await API.updateVisitorGuest(editingVisitor.id, data.formData)
      } else if (isGuestModal) {
        await API.addNewVisitorGuest(data.clientId, data.formData)
      } else {
        await API.addNewVisitorVendor(data.clientId, data)
      }

      toggleNewVisitorModalType()
      const grantedAccess = [
        values.keyAccess && t('visitors:permissionAccess.keylessEntryNoun'),
        values.keylessAccess && t('visitors:permissionAccess.physicalKeyNoun'),
        values.pinCode && t('visitors:permissionAccess.pinCodeNoun'),
      ].filter(Boolean)
      displaySnackbar(
        isUpdate
          ? t('visitors:snackbar.visitorUpdated')
          : t(
              grantedAccess.length
                ? 'visitors:snackbar.visitorAddedWithAccess'
                : 'visitors:snackbar.visitorAdded',
              {
                type: snackbarVisitorType,
                name: values.visitorName,
                access: grantedAccess.join(', '),
              },
            ),
      )
      onCreateSuccess && onCreateSuccess()
    } catch (err) {
      Sentry.captureException(err)
      displaySnackbar(
        getVisitorCreateErrorMessage({ t, error: err, isUpdate, snackbarVisitorType }),
        true,
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handlePrimaryButtonClick = (values) => {
    const shouldSubmitForm = (isFirstStep && !isRecuringType) || (isSecondStep && isRecuringType)

    if (!shouldSubmitForm) {
      setCurrentStep(1)
      return
    }

    handleSubmit(values)
  }

  const handleSecondaryButtonClick = () => {
    if (isFirstStep) {
      toggleNewVisitorModalType(modalTypeToGoBackTo)
      return
    }

    setCurrentStep(0)
  }

  if (!isOpen) {
    return null
  }

  return (
    <ModalContainer
      additionalClass="visitor-modal"
      onClose={() => toggleNewVisitorModalType()}
      headerTitle={modalHeader}
      headerIcon={<Users size={20} />}
    >
      <div className="visitor-modal__body">
        {isPrefillLoading ? (
          <VisitorLoader />
        ) : (
          <Formik
            enableReinitialize={true}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            validateOnChange={!isUpdate}
            validateOnBlur
          >
            {({ setFieldValue, values, isValid, handleSubmit, handleChange, errors }) => {
              const firstStepValid =
                (isGuestModal ? !!values.resident && !errors.resident : !errors.resident) &&
                !!values.visitorName &&
                !errors.visitorName &&
                !errors.visitorId &&
                (values.keyAccess
                  ? !!values.visitorPhone && !errors.visitorPhone
                  : !errors.visitorPhone) &&
                (values.keyAccess
                  ? !!values.visitorEmail && !errors.visitorEmail
                  : !errors.visitorEmail) &&
                (values.pinCode
                  ? !!values.visitorEmail && !errors.visitorEmail
                  : !errors.visitorEmail)

              const isPrimaryButtonDisabled = !(isFirstStep ? firstStepValid : isValid) || isLoading

              return (
                <Form className="new-visitor-form" onSubmit={handleSubmit}>
                  <div className="new-visitor-form__container">
                    {isRecuringType && (
                      <div className="columns">
                        <div className="column is-12">
                          <Stepper
                            steps={[firstStepName, t('visitors:form.permission')]}
                            currentStep={currentStep}
                          />
                        </div>
                      </div>
                    )}
                    {isFirstStep && (
                      <FirstStep
                        values={values}
                        setFieldValue={setFieldValue}
                        handleChange={handleChange}
                        isVendorModal={isVendorModal}
                        isEditMode={isUpdate}
                        inputLabelPrefix={firstStepLabelPrefix}
                        isRecuringType={isRecuringType}
                      />
                    )}
                    {isSecondStep && (
                      <SecondStep
                        values={values}
                        isVendorModal={isVendorModal}
                        isRecuringType={isRecuringType}
                        setFieldValue={setFieldValue}
                        selectedBuilding={selectedBuilding}
                      />
                    )}
                  </div>
                  <ModalButtons
                    primaryButtonText={primaryButtonText}
                    onPrimaryButtonClick={() => handlePrimaryButtonClick(values)}
                    primaryButtonDisabled={isPrimaryButtonDisabled}
                    secondaryButtonText={t('visitors:form.back')}
                    onSecondaryButtonClick={handleSecondaryButtonClick}
                  />
                </Form>
              )
            }}
          </Formik>
        )}
      </div>
    </ModalContainer>
  )
}

NewVisitorFormModal.propTypes = {
  isOpen: PropTypes.bool,
  visitorModalType: PropTypes.string,
  toggleNewVisitorModalType: PropTypes.func.isRequired,
  selectedBuilding: PropTypes.object,
  editingVisitor: PropTypes.object,
  onCreateSuccess: PropTypes.func,
}

const mapStateToProps = (state) => {
  return {
    selectedBuilding: selectActiveBuilding(state),
  }
}

export default connect(mapStateToProps)(NewVisitorFormModal)
